/**
 * A tool's arguments as labelled fields shaped by the inputs the tool takes: the required and filled
 * ones show, the other optional ones wait behind a pill. An argument the tool does not take shows so it
 * can be cleared, and a tool the account no longer has edits the call's arguments as they are. A field
 * emptied drops out.
 */

import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Field, FieldInput, FieldSelect, RevealGroup } from '@/modules/simulator/ui/field';
import { kindOfValue, type ToolField } from './tool-schema';

type Args = Record<string, unknown>;

const withValue = (args: Args, name: string, value: unknown): Args => {
	const next = { ...args };
	if (value === undefined) delete next[name];
	else next[name] = value;
	return next;
};

const NUMBER = /^-?\d+(\.\d+)?$/;
const WHOLE_NUMBER = /^-?\d+$/;

/** A value the call left out or passed as null reads as an empty field. */
const isBlank = (value: unknown): boolean => value === undefined || value === null;

const InvalidHint = ({ children }: { children: string }) => (
	<span className='text-[11px] leading-4 text-red-600'>{children}</span>
);

interface ValueInputProps {
	value: unknown;
	onChange: (value: unknown) => void;
}

// The two inputs below hold the text being typed and otherwise show the value.

/** Lands on blur; text that does not parse stays to be fixed. */
const JsonInput = ({ value, onChange }: ValueInputProps) => {
	const [editing, setEditing] = useState<string | null>(null);
	const [invalid, setInvalid] = useState(false);
	const commit = () => {
		if (editing === null) return;
		let parsed: unknown;
		try {
			parsed = editing.trim() ? JSON.parse(editing) : undefined;
		} catch {
			setInvalid(true);
			return;
		}
		onChange(parsed);
		setEditing(null);
		setInvalid(false);
	};
	return (
		<>
			<Textarea
				value={editing ?? (isBlank(value) ? '' : JSON.stringify(value, null, 2))}
				onChange={(event) => setEditing(event.target.value)}
				onBlur={commit}
				className='min-h-16 rounded-lg border-gray-500 font-mono text-xs'
			/>
			{invalid && <InvalidHint>Not valid JSON; the last valid value is kept.</InvalidHint>}
		</>
	);
};

/** Lands as it is typed once it reads as a number; a partial one (`-`, `1.`) gives way to the value on blur. */
const NumberInput = ({ value, integer, onChange }: ValueInputProps & { integer: boolean }) => {
	const [editing, setEditing] = useState<string | null>(null);
	const pattern = integer ? WHOLE_NUMBER : NUMBER;
	const invalid = editing !== null && editing.trim() !== '' && !pattern.test(editing.trim());
	return (
		<>
			<FieldInput
				value={editing ?? (isBlank(value) ? '' : String(value))}
				inputMode={integer ? 'numeric' : 'decimal'}
				onChange={(event) => {
					const next = event.target.value.trim();
					setEditing(event.target.value);
					if (!next) onChange(undefined);
					else if (pattern.test(next)) onChange(Number(next));
				}}
				onBlur={() => setEditing(null)}
			/>
			{invalid && (
				<InvalidHint>{`Not a ${integer ? 'whole number' : 'number'}; the last valid value is kept.`}</InvalidHint>
			)}
		</>
	);
};

const ArgumentInput = ({ field, value, onChange }: ValueInputProps & { field: ToolField }) => {
	const text = isBlank(value) ? '' : String(value);
	if (field.kind === 'choice') {
		return (
			<FieldSelect value={text} options={field.options} unsetLabel='Not set' onChange={(next) => onChange(next || undefined)} />
		);
	}
	if (field.kind === 'boolean') {
		return (
			<FieldSelect
				value={text}
				options={[
					{ value: 'true', label: 'Yes' },
					{ value: 'false', label: 'No' }
				]}
				unsetLabel='Not set'
				onChange={(next) => onChange(next ? next === 'true' : undefined)}
			/>
		);
	}
	if (field.kind === 'json') return <JsonInput value={value} onChange={onChange} />;
	if (field.kind === 'number' || field.kind === 'integer') {
		return <NumberInput value={value} integer={field.kind === 'integer'} onChange={onChange} />;
	}
	return <FieldInput value={text} onChange={(event) => onChange(event.target.value || undefined)} />;
};

const labelOf = (field: ToolField, notInputs: ReadonlySet<string>): string => {
	if (notInputs.has(field.name)) return `${field.name} · not an input of this tool`;
	return field.required ? `${field.name} · required` : field.name;
};

const ArgumentGrid = ({
	rows,
	args,
	notInputs,
	onChange
}: {
	rows: ToolField[];
	args: Args;
	notInputs: ReadonlySet<string>;
	onChange: (args: Args) => void;
}) => (
	<div className='grid grid-cols-[repeat(auto-fill,minmax(12rem,1fr))] gap-2'>
		{rows.map((field) => (
			<Field key={field.name} label={labelOf(field, notInputs)} className={cn(field.kind === 'json' && 'col-span-full')}>
				<ArgumentInput field={field} value={args[field.name]} onChange={(value) => onChange(withValue(args, field.name, value))} />
			</Field>
		))}
	</div>
);

interface ToolArgumentsProps {
	args: Args;
	/** The inputs the tool takes, or null when the account no longer has the tool. */
	fields: ToolField[] | null;
	onChange: (args: Args) => void;
}

export const ToolArguments = ({ args, fields, onChange }: ToolArgumentsProps) => {
	// Read once, so a field filled or emptied here stays where it is while it is edited.
	const [filledAtOpen] = useState(() => new Set(Object.keys(args).filter((name) => !isBlank(args[name]))));
	const known = fields ?? [];
	const knownNames = new Set(known.map((field) => field.name));
	const carried = Object.keys(args).filter((name) => !knownNames.has(name));
	const unlisted: ToolField[] = carried.map((name) => ({
		name,
		kind: kindOfValue(args[name]),
		required: false,
		description: null,
		options: []
	}));
	// Only a tool whose inputs are known can say an argument is not one of them.
	const notInputs = new Set(fields ? carried : []);
	const shown = [...known.filter((field) => field.required || filledAtOpen.has(field.name)), ...unlisted];
	const optional = known.filter((field) => !field.required && !filledAtOpen.has(field.name));

	return (
		<div className='flex flex-col gap-2'>
			{shown.length > 0 && <ArgumentGrid rows={shown} args={args} notInputs={notInputs} onChange={onChange} />}
			{optional.length > 0 && (
				<div className='flex flex-wrap gap-2'>
					<RevealGroup label={`Optional (${optional.length})`} filled={false}>
						<ArgumentGrid rows={optional} args={args} notInputs={notInputs} onChange={onChange} />
					</RevealGroup>
				</div>
			)}
			{shown.length === 0 && optional.length === 0 && (
				<p className='text-xs leading-4 text-gray-800'>{fields ? 'This tool takes no arguments.' : 'No arguments.'}</p>
			)}
		</div>
	);
};
